import apiClient from "./apiClient";

// Module-level cache: only one user is ever logged in per browser tab,
// so we can share a single in-flight promise across all hook instances
// (Sidebar, page component, Header) to avoid duplicate network calls.
let _profileCache = null;
let _profilePromise = null;

export const fetchUserProfile = async () => {
 if (_profileCache) return _profileCache;
 if (_profilePromise) return _profilePromise;
 _profilePromise = apiClient
  .get("/anything/common/profile")
  .then((r) => {
   _profileCache = r.data.data;
   _profilePromise = null;
   return _profileCache;
  })
  .catch((err) => {
   _profilePromise = null;
   throw err;
  });
 return _profilePromise;
};

export const clearProfileCache = () => {
 _profileCache = null;
 _profilePromise = null;
};

// Self-service profile (name + email, sourced from Keycloak)
export const fetchMyProfileDetails = async () => {
 const response = await apiClient.get("/anything/common/me");
 return response.data;
};

export const updateMyProfile = async ({ first_name, last_name, email }) => {
 const response = await apiClient.put("/anything/common/me", { first_name, last_name, email });
 return response.data;
};

export const changeMyPassword = async ({ current_password, new_password }) => {
 const response = await apiClient.put("/anything/common/me/password", { current_password, new_password });
 return response.data;
};

// User CRUD APIs (role-validated at API level)

export const createUser = async (userData) => {
 const response = await apiClient.post("/anything/common/users", userData);
 return response.data;
};

// Note: the RestoreCenter page applies any state filter for users
// client-side against the `state_id` field already present on each
// returned row (legacy/display-only column, unrelated to the
// state_id/district_id/block_id/school_id assignment-table filters below).
export const fetchUsers = async ({ page = 1, page_size = 10, search = "", role, enabled, include_deleted, deleted_only, state_id, district_id, block_id, school_id } = {}) => {
 const params = { page, page_size, search };
 if (role) params.role = role;
 if (enabled !== undefined && enabled !== null && enabled !== "") params.enabled = enabled;
 if (include_deleted !== undefined && include_deleted !== null && include_deleted !== "") params.include_deleted = include_deleted;
 if (deleted_only !== undefined && deleted_only !== null && deleted_only !== "") params.deleted_only = deleted_only;
 if (state_id) params.state_id = state_id;
 if (district_id) params.district_id = district_id;
 if (block_id) params.block_id = block_id;
 if (school_id) params.school_id = school_id;
 const response = await apiClient.get("/anything/common/users", { params });
 return response.data;
};

// Fetch a single user by Keycloak id — used to reopen the edit form on refresh.
export const fetchUserById = async (keycloakUserId) => {
 const response = await apiClient.get(`/anything/common/users/${keycloakUserId}`);
 return response.data;
};

// Resolve Keycloak user IDs to display names, e.g. for a "Deleted By" column
// that otherwise shows a raw Keycloak sub. Returns { [keycloakUserId]: name }.
export const resolveUserNames = async (ids) => {
 const uniqueIds = [...new Set((ids || []).filter(Boolean))];
 if (uniqueIds.length === 0) return {};
 const response = await apiClient.get("/anything/common/users/resolve", {
 params: { ids: uniqueIds.join(",") },
 });
 return response.data.data || {};
};

// User Delete APIs (role-validated at API level)

export const deleteUser = async (keycloakUserId) => {
 const response = await apiClient.delete(`/anything/common/users/${keycloakUserId}`);
 return response.data;
};

export const bulkDeleteUsers = async (userIds) => {
 const response = await apiClient.post("/anything/common/users/bulk-delete", {
 user_ids: userIds,
 });
 return response.data;
};

// Restore a soft-deleted user (role-validated at API level, same hierarchy as delete)
export const restoreUser = async (keycloakUserId) => {
 const response = await apiClient.post(`/anything/common/users/${keycloakUserId}/restore`);
 return response.data;
};

// User Update API (role-validated at API level)

export const updateUser = async (keycloakUserId, data) => {
 const response = await apiClient.put(`/anything/common/users/${keycloakUserId}`, data);
 return response.data;
};

// User Status APIs (role-validated at API level)

export const updateUserStatus = async (keycloakUserId, enabled) => {
 const response = await apiClient.put(`/anything/common/users/${keycloakUserId}/status`, {
 enabled,
 });
 return response.data;
};

// Bulk Upload Users (any role — same permission model as single create-user)

export const bulkUploadUsers = async (file) => {
 const formData = new FormData();
 formData.append("file", file);
 const response = await apiClient.post("/anything/common/bulk-upload-users", formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 return response.data;
};
